import type { HttpResponse } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { ClientHttpService } from '@data-services/http/client-http.service';
import { NameMapEntry } from '@models/common';
import { DoorMessage } from '@models/doors';
import { MessageStatus } from '@models/messages';
import { getFileNameFromContentDisposition } from '@utils';
import { DoorCount } from './door-count.model';
import { PrintDoorMessageRequest } from './print-door-message-request.model';
import { PrintDoorMessageResponse } from './print-door-message-response.model';

@Service()
export class DoorMessageDataService {
  private readonly http = inject(ClientHttpService);

  async getMessageCountsAsync(projectId: string, inboxId: string): Promise<DoorCount[]> {
    return await this.http.getObjectAsync<DoorCount[]>(`projects/${projectId}/inbox/${inboxId}/doormessages/count`);
  }

  async getUnhandledMessageCountsAsync(projectId: string, inboxId: string): Promise<DoorCount[]> {
    return await this.http.getObjectAsync<DoorCount[]>(`projects/${projectId}/inbox/${inboxId}/doormessages/unhandled/count`);
  }

  async getInboxMessagesAsync(projectId: string, inboxId: string): Promise<DoorMessage[]> {
    return await this.http.getObjectAsync<DoorMessage[]>(`project/${projectId}/doormessages/inbox/${inboxId}`);
  }

  async getDoorMessagesAsync(projectId: string, inboxId: string, doorId: string): Promise<DoorMessage[]> {
    return await this.http.getObjectAsync<DoorMessage[]>(`project/${projectId}/doormessages/inbox/${inboxId}/doors/${doorId}`);
  }

  async getMessageUserNamesAsync(projectId: string): Promise<NameMapEntry[]> {
    return await this.http.getObjectAsync<NameMapEntry[]>(`project/${projectId}/doors/messages/users`);
  }

  async getMessageImageAsync(projectId: string, messageId: string): Promise<string> {
    return await this.http.getObjectAsync<string>(`projects/${projectId}/doormessages/${messageId}/view/image`);
  }

  async getMessageCopiesAsync(projectId: string, messageId: string): Promise<DoorMessage[]> {
    return await this.http.getObjectAsync<DoorMessage[]>(`projects/${projectId}/doors/messages/${messageId}/copies`);
  }

  async printMessageAsync(projectId: string, messageId: string, request: PrintDoorMessageRequest, fallbackFileName: string): Promise<PrintDoorMessageResponse> {
    const response: HttpResponse<Blob> = await this.http.postBlobAsync<PrintDoorMessageRequest>(
      `projects/${projectId}/doormessages/${messageId}/pdf`, request);
    const blob = response.body ?? new Blob();
    const fileName = getFileNameFromContentDisposition(response.headers.get('Content-Disposition'), fallbackFileName);
    return { blob, fileName };
  }

  async handleMessageAsync(messageId: string, status: MessageStatus): Promise<DoorMessage> {
    return await this.http.putObjectAsync<string, DoorMessage>(`project/doormessages/${messageId}/status/${status}/handle`, '');
  }

  async sendMessagesAsync(messages: DoorMessage[]): Promise<number> {
    return await this.http.postObjectAsync<DoorMessage[], number>('projects/doors/doormessages/bulk', messages);
  }
}
